import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchProjects } from '../../Redux/Projects/projectSlice';
import { FaChevronRight, FaCalendarAlt, FaMoneyBillWave } from 'react-icons/fa';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import dummyImage from '../../assets/images/projects_placeholder.jpg';
import '../../Stylesheets/Dashboard/Projects.css';
import NoProjectsView from './NoProjectsView';

const Projects = () => {
  const dispatch = useDispatch();
  const { list: allProjects, status } = useSelector((state) => state.projects);

  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('latest');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  useEffect(() => {
    dispatch(fetchProjects());
  }, [dispatch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortBy]);

  const hasAnyProjects = allProjects && allProjects.length > 0;

  const filteredProjects = (allProjects || [])
    .filter((project) =>
      project.name?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'budget') return (b.budget || 0) - (a.budget || 0);
      if (sortBy === 'oldest') return new Date(a.startDate) - new Date(b.startDate);
      return new Date(b.startDate) - new Date(a.startDate); // latest first
    });

  const paginatedProjects = filteredProjects.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);

  if (status === 'loading') {
    return (
      <div className="projects-grid">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="project-card">
            <Skeleton height={140} borderRadius={4} />
            <div className="project-content">
              <h3><Skeleton width="60%" /></h3>
              <p><Skeleton width="80%" /></p>
              <p><Skeleton width="40%" /></p>
              <p><Skeleton width="50%" /></p>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // If no projects exist at all (first-time state)
  if (!hasAnyProjects) {
    return (
      <NoProjectsView/>
    );
  }

  // Otherwise: always show header + controls
  return (
    <div className="projects-page-wrapper">
      <div className="projects-header">
        <h2>All Projects</h2>

        <div className="projects-controls">
          {/* clearable search input */}
          <div className="search-input-wrap">
            <input
              className="search-input"
              type="text"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setSearchTerm('');
              }}
            />
            {searchTerm && (
              <button
                type="button"
                className="clear-search-btn"
                aria-label="Clear search"
                onClick={() => setSearchTerm('')}
              >
                ×
              </button>
            )}
          </div>

          <div className="project-sort-wrapper">
            <select
              className="project-sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="latest">Sort by Latest</option>
              <option value="oldest">Sort by Oldest</option>
              <option value="budget">Sort by Budget</option>
            </select>
          </div>

          <Link to="/dashboard/create">
            <button className="create-project-btn">+ Create New Project</button>
          </Link>
        </div>
      </div>

      {/* If search yields no matches, show an inline “no results” panel, not the first-time empty state */}
      {filteredProjects.length === 0 ? (
        <div className="no-results-panel">
          <h3>No matches for “{searchTerm}”</h3>
          <p>Try a different keyword or clear the search.</p>
          <div className="no-results-actions">
            <button className="page-btn ghost" onClick={() => setSearchTerm('')}>
              Clear Search
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="projects-grid">
            {paginatedProjects.map((project) => (
              <div key={project._id} className="project-card">
                <div
                  className="project-image"
                  style={{
                    backgroundImage: `url(${project.image || dummyImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  <div className="project-status">{project.status || 'Active'}</div>
                </div>

                <div className="project-content">
                  <h3>{project.name}</h3>
                  <p>{project.location}</p>
                  <div className="project-meta-combined">
                    <span className="meta-item">
                      <FaCalendarAlt /> {new Date(project.startDate).toLocaleDateString('en-IN')}
                    </span>
                    <span className="meta-item">
                      <FaMoneyBillWave /> ₹{Number(project.budget).toLocaleString('en-IN')}
                    </span>
                  </div>
                </div>

                <Link to={`/dashboard/project/${project._id}`} className="no-link-style">
                  <button className="view-project-btn">
                    <span>View Project</span>
                    <FaChevronRight />
                  </button>
                </Link>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="pagination-wrap" role="navigation" aria-label="Project pagination">
              <div className="pagination-stats">
                {(() => {
                  const start = (currentPage - 1) * itemsPerPage + 1;
                  const end = Math.min(currentPage * itemsPerPage, filteredProjects.length);
                  return `Showing ${start}–${end} of ${filteredProjects.length}`;
                })()}
              </div>

              <div className="pagination-controls pretty-scroll">
                <button
                  className="page-btn ghost"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  aria-label="First page"
                  title="First"
                >
                  «
                </button>

                <button
                  className="page-btn ghost"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                  title="Previous"
                >
                  ‹
                </button>

                {(() => {
                  const maxLen = 7;
                  const pages = [];
                  const add = (v) => pages.push(v);

                  if (totalPages <= maxLen) {
                    for (let i = 1; i <= totalPages; i++) add(i);
                  } else {
                    const left = Math.max(2, currentPage - 1);
                    const right = Math.min(totalPages - 1, currentPage + 1);

                    add(1);
                    if (left > 2) add('…');
                    for (let i = left; i <= right; i++) add(i);
                    if (right < totalPages - 1) add('…');
                    add(totalPages);
                  }

                  return pages.map((p, idx) =>
                    p === '…' ? (
                      <span key={`e-${idx}`} className="page-ellipsis" aria-hidden="true">…</span>
                    ) : (
                      <button
                        key={p}
                        className={`page-btn ${currentPage === p ? 'active' : ''}`}
                        onClick={() => setCurrentPage(p)}
                        aria-current={currentPage === p ? 'page' : undefined}
                        aria-label={`Page ${p}`}
                      >
                        {p}
                      </button>
                    )
                  );
                })()}

                <button
                  className="page-btn ghost"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                  title="Next"
                >
                  ›
                </button>

                <button
                  className="page-btn ghost"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  aria-label="Last page"
                  title="Last"
                >
                  »
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Projects;

